/**
 * @file     FaqBlock.tsx
 * @purpose  Renders a content-engine faq block with the hub FAQ accordion: a
 *           visible heading, the first answer open, and every answer kept in
 *           the DOM so the article's FAQPage schema is backed by visible text.
 */
'use client';

import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { FaqList } from '@/components/hub/FaqList';
import { headingFont, hubColors } from '@/components/hub/hubKitStyles';
import type { ContentBlock } from '@/services/content-engine/types';

type FaqBlockProps = Extract<ContentBlock, { t: 'faq' }>;

export const FaqBlock = ({ items }: FaqBlockProps) => {
  const { t } = useTranslation();
  if (items.length === 0) return null;

  return (
    <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        component="h2"
        sx={{ m: 0, fontFamily: headingFont, fontSize: { xs: 22, md: 26 }, fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.2, color: hubColors.textPrimary }}
      >
        {t('blog.blocks.faq.heading', { defaultValue: 'Common questions' })}
      </Box>
      <FaqList
        defaultExpandedIndex={0}
        items={items.map((item) => ({ question: item.q, answer: item.a }))}
      />
    </Box>
  );
};
